import { THEME_IDS } from './themeIds.js';

/**
 * Master switch for the big-top pilot rollout.
 * Roll back: set to false and reload.
 */
export const ENABLE_BIG_TOP_THEME = true;

export const DERIVATIVES_BIG_TOP_ROUTE_PATHS = [
  '/derivatives',
  '/productQuotientRule',
  '/trigonometricDerivatives',
  '/naturalDerivatives',
];

/**
 * Skill routes that receive big-top skill chrome when the pilot is enabled.
 * Add routes here as shells are wired (Phase 2+ batch rollout).
 */
export const SKILL_BIG_TOP_ROUTE_PATHS = [
  ...DERIVATIVES_BIG_TOP_ROUTE_PATHS,
  '/exponentsVariety',
  '/integration',
  '/integrationAccumulation',
  '/unitCircle',
  '/cubesAndSquares',
  '/identifyingFunctions',
  '/plottingPoints',
  '/plottingPointsPolynomials',
  '/functionNotation',
  '/factoringQuadratics',
  '/equationsOfLines',
  '/averageRateOfChange',
  '/limitsLanguage',
  '/limitsGraphs',
  '/limitsTables',
  '/limitsExpressions',
  '/limitsMix',
  '/limitsProperties',
  '/limitsContinuity',
  '/limitsRemovable',
  '/limitsIntervalContinuity',
  '/limitsIVT',
  '/limitsSqueeze',
  '/limitsProcedureMix',
  '/limitsDividingOut',
  '/separationOfVariables',
  '/curveSketchingIntroTutorial',
];

/** App pages that use the big-top shell when the pilot is enabled. */
export const BIG_TOP_APP_ROUTE_PATHS = [
  '/',
  '/viewProfile',
  '/userProgress',
  '/courseHome',
  '/loginWithGoogle',
  '/login/email',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/logout',
  '/account-deleted',
  '/sectionHome',
  '/topicsHome',
  '/skillsHome',
  '/subskillsHome',
  '/skillFrame',
  '/class-invite',
  '/about',
  '/contact',
];

/**
 * Info and marketing pages that use the cream shell (not dark big-top canvas).
 * Applied synchronously on `.appContent` in App so there is no green/dark flash
 * during lazy load or markdown fetch.
 */
export const CREAM_SHELL_ROUTE_PATHS = [
  '/about',
  '/contact',
  '/privacy',
  '/termsofservice',
  '/plaintermsofservice',
];

/** Review card routes (selection, sessions, active session, completion). */
export const REVIEW_BIG_TOP_ROUTE_PATHS = ['/reviewCards'];

/**
 * Site-admin and legacy landing routes still on cream big-top.
 * Teacher class views → TEACHER_VIEW_ROUTE_PATHS in teacherViewRollout.js.
 */
export const TEACHER_ADMIN_BIG_TOP_ROUTE_PATHS = [
  '/calculusHome',
  '/admin',
  '/ccAdmin',
];

export function isTeacherAdminBigTopRoute(pathname) {
  return TEACHER_ADMIN_BIG_TOP_ROUTE_PATHS.some((route) => matchesAppRoute(pathname, route));
}

export function isReviewBigTopRoute(pathname) {
  if (!ENABLE_BIG_TOP_THEME) {
    return false;
  }

  return REVIEW_BIG_TOP_ROUTE_PATHS.some((route) => matchesAppRoute(pathname, route));
}

export function matchesAppRoute(pathname, route) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

export function isBigTopAppRoute(pathname) {
  return BIG_TOP_APP_ROUTE_PATHS.some((route) => matchesAppRoute(pathname, route));
}

export function isCreamShellRoute(pathname) {
  return CREAM_SHELL_ROUTE_PATHS.some((route) => matchesAppRoute(pathname, route));
}

/** Skill IDs served by the derivatives big-top pilot (completion-flow theming). */
export function isDerivativesBigTopSkillId(skillId) {
  const id = Number(skillId);
  if (!Number.isFinite(id)) {
    return false;
  }

  if (id >= 2011 && id <= 2050) return true;
  if (id === 2080 || id === 2090) return true;
  if (id >= 2110 && id <= 2120) return true;
  if (id >= 2210 && id <= 2350) return true;
  if (id >= 2410 && id <= 2420) return true;

  return false;
}

export function isDerivativesBigTopPilotRoute(pathname) {
  return DERIVATIVES_BIG_TOP_ROUTE_PATHS.includes(pathname);
}

export function isBigTopSkillRoute(pathname) {
  if (!ENABLE_BIG_TOP_THEME) {
    return false;
  }

  return SKILL_BIG_TOP_ROUTE_PATHS.some((route) => matchesAppRoute(pathname, route));
}

export function shouldApplyBigTopSkillTheme(pathname) {
  return isBigTopSkillRoute(pathname);
}

export function isDerivativesBigTopRoute(pathname) {
  return ENABLE_BIG_TOP_THEME && isDerivativesBigTopPilotRoute(pathname);
}

function isBigTopCompletionFlowRoute(pathname, locationState) {
  if (!ENABLE_BIG_TOP_THEME) {
    return false;
  }

  if (pathname !== '/skillComplete' && pathname !== '/progressFeedback') {
    return false;
  }

  if (locationState?.bigTopTheme === true || locationState?.theme === THEME_IDS.BIG_TOP) {
    return true;
  }

  return isDerivativesBigTopSkillId(locationState?.skillId);
}

export function isBigTopSkillCompleteRoute(pathname, locationState) {
  return pathname === '/skillComplete' && isBigTopCompletionFlowRoute(pathname, locationState);
}

export function isBigTopProgressFeedbackRoute(pathname, locationState) {
  return pathname === '/progressFeedback' && isBigTopCompletionFlowRoute(pathname, locationState);
}

export function shouldApplyBigTopAppTheme(pathname, locationState) {
  return shouldApplyBigTopSkillTheme(pathname)
    || isBigTopCompletionFlowRoute(pathname, locationState)
    || (ENABLE_BIG_TOP_THEME && isBigTopAppRoute(pathname))
    || (ENABLE_BIG_TOP_THEME && isTeacherAdminBigTopRoute(pathname))
    || isReviewBigTopRoute(pathname);
}

/**
 * Whether route transitions should show the big-top loader instead of plain
 * "Loading..." text on the default app shell background.
 *
 * Covers Suspense chunk loads, auth gates (Protected), and skill init gates that
 * reuse the same component.
 */
export function shouldUseBigTopRouteLoader(pathname, locationState) {
  if (!ENABLE_BIG_TOP_THEME) {
    return false;
  }

  if (shouldApplyBigTopAppTheme(pathname, locationState)) {
    return true;
  }

  // Completion-flow pages: use big-top loader during chunk fetch even when
  // location.state is not yet available (e.g. hard refresh).
  if (pathname === '/skillComplete' || pathname === '/progressFeedback') {
    return true;
  }

  return false;
}

/**
 * @param {import('./themeIds.js').THEME_IDS[keyof import('./themeIds.js').THEME_IDS]} themeId
 * @param {string} pathname
 * @param {object | null | undefined} locationState
 */
export function shouldApplyThemeToRoute(themeId, pathname, locationState) {
  if (themeId === THEME_IDS.BIG_TOP) {
    return shouldApplyBigTopAppTheme(pathname, locationState);
  }

  return false;
}
